import React from 'react';
import { Mail, Phone, MapPin, Clock, Calendar, Briefcase, Network, ExternalLink, Users } from 'lucide-react';
import { professionalData } from '../professionalData';

const ContactPage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="section" style={{ paddingTop: '6rem', paddingBottom: '4rem' }} data-testid="contact-hero">
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 style={{ marginBottom: '1rem' }}>
            Collaborate & Connect
          </h1>
          <p style={{ 
            fontSize: '1.25rem',
            color: 'var(--accent)',
            fontWeight: 500,
            maxWidth: '700px',
            margin: '0 auto 3rem'
          }}>
            Located in Lucknow, India | Available for Global Remote Consultations
          </p>
        </div>
      </section>

      {/* Triage Grid - 3 Consultation Pathways */}
      <section className="section" style={{ paddingTop: '0', paddingBottom: '4rem' }} data-testid="triage-grid">
        <div className="container">
          <div className="grid-3" style={{ gap: '2rem', alignItems: 'stretch' }}>
            
            {/* CARD 1: Family & Pediatric Consultations (B2C) */}
            <div className="glass-card" style={{ 
              display: 'flex', 
              flexDirection: 'column',
              padding: '2.5rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 1.5rem',
                background: 'linear-gradient(135deg, var(--accent) 0%, #D4A574 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Users size={36} color="white" />
              </div>
              
              <h3 style={{ 
                fontSize: '1.5rem',
                fontFamily: 'var(--font-heading)',
                fontWeight: 600,
                marginBottom: '1rem',
                color: 'var(--primary)'
              }}>
                Clinical Consultations for Families
              </h3>
              
              <p style={{
                fontSize: '1rem',
                lineHeight: 1.8,
                color: 'var(--text-main)',
                marginBottom: '2rem',
                flex: 1
              }}>
                For parents seeking high-resolution diagnostic mapping (<strong>ADOS-2</strong>, <strong>WISC-IV/V</strong>) or functional behavioral intervention (<strong>ABA/PBS</strong>). We move beyond standard reports to deliver an empirical roadmap for your child&apos;s neuro-developmental journey.
              </p>
              
              <a
                href="https://consult.modernminds.co.in"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '1rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
                data-testid="book-consultation"
              >
                <Calendar size={20} />
                Book a Clinical Consultation
              </a>
            </div>

            {/* CARD 2: Clinical Partnerships & B2B Diagnostics */}
            <div className="glass-card" style={{ 
              display: 'flex', 
              flexDirection: 'column',
              padding: '2.5rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 1.5rem',
                background: 'linear-gradient(135deg, #1E3A8A 0%, #3B5BA5 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Briefcase size={36} color="white" />
              </div>
              
              <h3 style={{ 
                fontSize: '1.5rem',
                fontFamily: 'var(--font-heading)',
                fontWeight: 600,
                marginBottom: '1rem',
                color: 'var(--primary)'
              }}>
                Professional Referral Network
              </h3>
              
              <p style={{
                fontSize: '1rem',
                lineHeight: 1.8,
                color: 'var(--text-main)',
                marginBottom: '2rem',
                flex: 1
              }}>
                I partner with medical institutions, pediatricians, and educational frameworks to provide outsourced, high-fidelity psychometric evaluations. I deliver structured, data-driven Clinical Scorecards that empower your team to make definitive management decisions based on gold-standard diagnostics.
              </p>
              
              <a
                href="mailto:isha@modernminds.co.in?subject=B2B%20Diagnostic%20Referral%20Inquiry"
                className="btn-primary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '1rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: 'var(--primary)',
                  border: 'none'
                }}
                data-testid="b2b-inquiry"
              >
                <Mail size={20} />
                Inquire About B2B Diagnostics
              </a>
            </div>

            {/* CARD 3: Academic & Research Collaborations */}
            <div className="glass-card" style={{ 
              display: 'flex', 
              flexDirection: 'column',
              padding: '2.5rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 1.5rem',
                background: 'linear-gradient(135deg, #8F9C82 0%, #A8B89E 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Network size={36} color="white" />
              </div>
              
              <h3 style={{ 
                fontSize: '1.5rem',
                fontFamily: 'var(--font-heading)',
                fontWeight: 600,
                marginBottom: '1rem',
                color: 'var(--primary)'
              }}>
                Research & Academic Innovation
              </h3>
              
              <p style={{
                fontSize: '1rem',
                lineHeight: 1.8,
                color: 'var(--text-main)',
                marginBottom: '2rem',
                flex: 1
              }}>
                As an active researcher focusing on advancing neuro-affirming practices, I am seeking collaborations utilizing <strong>FACS analysis</strong>, <strong>predictive behavioral modeling</strong>, and <strong>cross-cultural developmental psychology</strong>. Let&apos;s advance the field together.
              </p>
              
              <a
                href="mailto:isha@modernminds.co.in?subject=Research%20Collaboration%20Inquiry"
                className="btn-primary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '1rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: 'transparent',
                  border: '2px solid var(--accent)',
                  color: 'var(--accent)'
                }}
                data-testid="research-inquiry"
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'var(--accent)';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--accent)';
                }}
              >
                <ExternalLink size={20} />
                Submit Research Inquiry
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="section-alt" data-testid="contact-info">
        <div className="container">
          <div style={{ 
            maxWidth: '900px', 
            margin: '0 auto',
            textAlign: 'center',
            marginBottom: '3rem'
          }}>
            <h2 style={{ marginBottom: '1rem' }}>
              Get in Touch
            </h2>
            <p style={{ 
              fontSize: '1.125rem',
              color: 'var(--text-muted)',
              lineHeight: 1.8
            }}>
              For general inquiries or immediate assistance, reach out through any of these channels.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '2rem',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            {/* Email */}
            <div className="glass-card" style={{ 
              padding: '2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                margin: '0 auto 1rem',
                background: 'var(--accent)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Mail size={28} color="white" />
              </div>
              <h4 style={{ 
                fontSize: '1.125rem',
                fontWeight: 600,
                marginBottom: '0.5rem',
                color: 'var(--primary)'
              }}>
                Email
              </h4>
              <a 
                href={`mailto:${professionalData.contact.email}`}
                style={{
                  color: 'var(--accent)',
                  textDecoration: 'none',
                  fontSize: '1rem'
                }}
              >
                {professionalData.contact.email}
              </a>
            </div>

            {/* Phone */}
            <div className="glass-card" style={{ 
              padding: '2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                margin: '0 auto 1rem',
                background: 'var(--accent)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Phone size={28} color="white" />
              </div>
              <h4 style={{ 
                fontSize: '1.125rem',
                fontWeight: 600,
                marginBottom: '0.5rem',
                color: 'var(--primary)'
              }}>
                Phone
              </h4>
              <a 
                href={`tel:${professionalData.contact.phone}`}
                style={{
                  color: 'var(--accent)',
                  textDecoration: 'none',
                  fontSize: '1rem'
                }}
              >
                {professionalData.contact.phone}
              </a>
            </div>

            {/* Location */}
            <div className="glass-card" style={{ 
              padding: '2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                margin: '0 auto 1rem',
                background: 'var(--accent)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <MapPin size={28} color="white" />
              </div>
              <h4 style={{ 
                fontSize: '1.125rem',
                fontWeight: 600,
                marginBottom: '0.5rem',
                color: 'var(--primary)'
              }}>
                Location
              </h4>
              <p style={{
                color: 'var(--text-main)',
                fontSize: '1rem',
                lineHeight: 1.6
              }}>
                {professionalData.contact.address}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Clinic Hours Section */}
      <section className="section" data-testid="clinic-hours">
        <div className="container">
          <div className="glass-card-strong" style={{ 
            maxWidth: '600px',
            margin: '0 auto',
            padding: '2.5rem',
            textAlign: 'center'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 1.5rem',
              background: 'var(--primary)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Clock size={36} color="white" />
            </div>
            <h3 style={{ 
              fontSize: '1.75rem',
              fontFamily: 'var(--font-heading)',
              marginBottom: '1.5rem',
              color: 'var(--primary)'
            }}>
              Consultation Hours
            </h3>
            <div style={{
              fontSize: '1.125rem',
              lineHeight: 2,
              color: 'var(--text-main)'
            }}>
              <p style={{ marginBottom: '0.5rem' }}>
                <strong>Monday – Friday:</strong> 10 AM to 8 PM
              </p>
              <p>
                <strong>Saturday and Sunday:</strong> Online Consultations Available
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Modern Minds CTA */}
      <section className="section-alt" data-testid="clinic-cta">
        <div className="container">
          <div style={{ 
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: '1rem' }}>
              Visit Modern Minds
            </h2>
            <p style={{ 
              fontSize: '1.125rem',
              lineHeight: 1.8,
              color: 'var(--text-muted)',
              marginBottom: '2rem'
            }}>
              For comprehensive clinical services, psychometric assessments, and behavioral therapy programs, visit our dedicated clinic website.
            </p>
            <a
              href={professionalData.clinic.website}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '1.125rem',
                padding: '1rem 2rem'
              }}
              data-testid="clinic-website-link"
            >
              Visit Modern Minds Clinic
              <ExternalLink size={20} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
